/**
 * PWA install and update policy. R1 Step 15.
 *
 * Hard rule 1: the application may fetch its own static assets and check for a
 * newer version of itself. Nothing else. No CDN, no remote font, no external
 * API, no third-party script, and **no user content may ride along on an
 * update request**.
 *
 * The policy is expressed as a pure predicate so it can be tested directly and
 * enforced in one place, rather than being a convention the service worker is
 * trusted to follow.
 */

export type RequestVerdict = "allowed" | "blocked";

export interface OutboundRequest {
  readonly body: string | null;
  readonly method: string;
  readonly url: string;
}

export interface PolicyDecision {
  /** Why it was blocked. Empty when allowed. */
  readonly reason: string;
  readonly verdict: RequestVerdict;
}

/** The one file the update check is allowed to read. */
export const VERSION_MANIFEST_PATH = "/version.json";

function sameOrigin(requestUrl: string, appOrigin: string): boolean {
  try {
    return new URL(requestUrl, appOrigin).origin === new URL(appOrigin).origin;
  } catch {
    return false;
  }
}

/**
 * Decides whether the application may make a request.
 *
 * Allowed: a GET, to the application's own origin, with no query string, no
 * fragment and no body. That is exactly "its own static assets and a version
 * check", and nothing else fits through it.
 *
 * A query string is refused even on the app's own origin, because a query
 * string is the easiest way for content to ride along on an update request
 * without anyone noticing.
 */
export function decideRequest(
  request: OutboundRequest,
  appOrigin: string,
): PolicyDecision {
  if (request.method.toUpperCase() !== "GET") {
    return {
      verdict: "blocked",
      reason: `Only GET is permitted; ${request.method.toUpperCase()} can carry a payload.`,
    };
  }
  if (request.body !== null && request.body !== "") {
    return {
      verdict: "blocked",
      reason: "A request body can carry user content.",
    };
  }
  if (!sameOrigin(request.url, appOrigin)) {
    return {
      verdict: "blocked",
      reason: "Only the application's own origin is permitted.",
    };
  }

  let parsed: URL;
  try {
    parsed = new URL(request.url, appOrigin);
    /* v8 ignore next 3 -- sameOrigin already parsed this successfully */
  } catch {
    return { verdict: "blocked", reason: "The address could not be read." };
  }
  if (parsed.search !== "") {
    return {
      verdict: "blocked",
      reason: "A query string can carry user content on an update request.",
    };
  }
  if (parsed.hash !== "") {
    return {
      verdict: "blocked",
      reason: "A fragment can carry user content on an update request.",
    };
  }
  return { verdict: "allowed", reason: "" };
}

export interface VersionManifest {
  readonly schemaVersion: 1;
  readonly builtAt: string;
  readonly version: string;
}

export function isVersionManifest(value: unknown): value is VersionManifest {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate.schemaVersion === 1 &&
    typeof candidate.version === "string" &&
    candidate.version.trim() !== "" &&
    typeof candidate.builtAt === "string" &&
    candidate.builtAt.trim() !== ""
  );
}

/**
 * Whether a newer build is available. Compared as opaque strings, because the
 * version is whatever the build stamped and this must never guess at ordering.
 */
export function updateAvailable(
  installed: string,
  advertised: string,
): boolean {
  return (
    installed.trim() !== "" &&
    advertised.trim() !== "" &&
    installed !== advertised
  );
}
